import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Snowflake, Bell, LogOut, User, LayoutDashboard, Menu } from "lucide-react";
import { initials } from "@/lib/format";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  const loadUnread = useCallback(async () => {
    if (!user || !user.role) return;
    try {
      const { data } = await api.get("/notifications");
      setUnread(data.filter((n) => !n.read).length);
    } catch (e) {}
  }, [user]);

  useEffect(() => { loadUnread(); }, [loadUnread, location.pathname]);

  useEffect(() => {
    const h = () => loadUnread();
    window.addEventListener("notif-refresh", h);
    return () => window.removeEventListener("notif-refresh", h);
  }, [loadUnread]);

  const navItems = user && user.role ? [
    { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
    ...(user.role === "student" ? [{ label: "Find Instructors", to: "/instructors", icon: User }] : []),
  ] : [];

  return (
    <header className="sticky top-0 z-50 glass bg-white/70 dark:bg-slate-900/60 border-b border-white/40 dark:border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to={user && user.role ? "/dashboard" : "/"} className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-xl bg-arctic-secondary flex items-center justify-center">
            <Snowflake className="w-5 h-5 text-arctic-accent group-hover:rotate-90 transition-transform duration-500" />
          </div>
          <span className="font-heading font-extrabold text-lg text-arctic-secondary dark:text-arctic-accent tracking-tight">
            Arctic<span className="text-arctic-primary">Drive</span>
          </span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          {navItems.map((item) => (
            <Button key={item.to} variant="ghost" size="sm" onClick={() => navigate(item.to)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className="hidden sm:inline-flex text-arctic-secondary dark:text-slate-200 hover:text-arctic-primary">
              {item.label}
            </Button>
          ))}

          {user && user.role ? (
            <>
              <Button variant="ghost" size="icon" className="relative" onClick={() => navigate("/notifications")} data-testid="nav-notifications">
                <Bell className="w-5 h-5" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">
                    {unread}
                  </span>
                )}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full outline-none focus:ring-2 focus:ring-arctic-primary" data-testid="nav-user-menu">
                    <Avatar className="w-9 h-9 border border-border">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="bg-arctic-primary text-white text-sm">{initials(user.name)}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-heading font-semibold">{user.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{user.role}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="w-4 h-4 mr-2" />Dashboard</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User className="w-4 h-4 mr-2" />Profile</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/notifications")} data-testid="menu-notifications"><Bell className="w-4 h-4 mr-2" />Notifications</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="nav-login">Log in</Button>
              <Button size="sm" className="rounded-full bg-arctic-primary hover:bg-arctic-primary/90" onClick={() => navigate("/register")} data-testid="nav-register">Get started</Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
